import { LocationKind, LocationPageAdmin, LocationPageInput } from '../../../types/location';

/** Estado do formulário: tudo texto, para os campos poderem ficar vazios. */
export interface LocationForm {
  name: string;
  slug: string;
  kind: LocationKind;
  city: string;
  neighborhoods: string[];
  title_terms: string[];
  headline: string;
  intro: string;
  highlights: { title: string; text: string }[];
  faq: { question: string; answer: string }[];
  hero_image_url: string;
  latitude: string;
  longitude: string;
  seo_title: string;
  seo_description: string;
  sort_order: string;
  is_published: boolean;
}

export const emptyForm = (): LocationForm => ({
  name: '',
  slug: '',
  kind: 'condominio',
  city: '',
  neighborhoods: [],
  title_terms: [],
  headline: '',
  intro: '',
  highlights: [],
  faq: [],
  hero_image_url: '',
  latitude: '',
  longitude: '',
  seo_title: '',
  seo_description: '',
  sort_order: '0',
  is_published: false,
});

export const formFromPage = (page: LocationPageAdmin): LocationForm => ({
  name: page.name,
  slug: page.slug,
  kind: page.kind,
  city: page.city || '',
  neighborhoods: [...page.neighborhoods],
  title_terms: [...page.title_terms],
  headline: page.headline || '',
  intro: page.intro || '',
  highlights: page.highlights.map((item) => ({ ...item })),
  faq: page.faq.map((item) => ({ ...item })),
  hero_image_url: page.hero_image_url || '',
  latitude: page.latitude != null ? String(page.latitude) : '',
  longitude: page.longitude != null ? String(page.longitude) : '',
  seo_title: page.seo_title || '',
  seo_description: page.seo_description || '',
  sort_order: String(page.sort_order ?? 0),
  is_published: page.is_published,
});

const textOrNull = (value: string): string | null => value.trim() || null;

const numberOrNull = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const cleanList = (values: string[]): string[] =>
  values.map((value) => value.trim()).filter(Boolean);

export const inputFromForm = (form: LocationForm): LocationPageInput => ({
  name: form.name.trim(),
  slug: textOrNull(form.slug),
  kind: form.kind,
  city: textOrNull(form.city),
  neighborhoods: cleanList(form.neighborhoods),
  title_terms: cleanList(form.title_terms),
  headline: textOrNull(form.headline),
  intro: textOrNull(form.intro),
  highlights: form.highlights
    .map((item) => ({ title: item.title.trim(), text: item.text.trim() }))
    .filter((item) => item.title),
  faq: form.faq
    .map((item) => ({ question: item.question.trim(), answer: item.answer.trim() }))
    .filter((item) => item.question && item.answer),
  hero_image_url: textOrNull(form.hero_image_url),
  latitude: numberOrNull(form.latitude),
  longitude: numberOrNull(form.longitude),
  seo_title: textOrNull(form.seo_title),
  seo_description: textOrNull(form.seo_description),
  sort_order: Math.trunc(numberOrNull(form.sort_order) ?? 0),
  is_published: form.is_published,
});

/** Mensagem do backend (422 do pydantic ou detail simples) em uma linha. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string; loc?: (string | number)[] };
    const field = (first.loc || []).filter((part) => part !== 'body').join('.');
    return field ? `${field}: ${first.msg}` : first.msg || fallback;
  }
  return fallback;
};
